import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AddEventLifeLine,
  EventLifeLine,
  SubEventLifeLine,
} from '../../models/life-line-models';
import { LabelService } from '../../services/label.service';
import { PatientService } from '../../services/patient.service';
import { ErrorPanelService } from '../../services/error-panel.service';
import moment from 'moment';

@Component({
  selector: 'app-significant-events-section',
  standalone: false,
  templateUrl: './significant-events-section.component.html',
  styleUrl: './significant-events-section.component.scss',
})
export class SignificantEventsSectionComponent implements OnInit {
  @Input() significantEvents: EventLifeLine[] = [];
  @Input() hasModifyButtonBeenClicked: boolean = false;
  @Input() patientId: string = '';
  @Output() refreshEventList = new EventEmitter<void>();

  editOrDeleteString: string = 'edit';
  expandedEvents: { [key: string]: boolean } = {};
  showEditPanel: boolean = false;

  panelTitle: string = 'Modifica Evento';
  eventTitle: string = '';
  eventStartDay: string = '';
  eventStartMonth: string = '';
  eventStartYear: string = '';
  eventEndDay: string = '';
  eventEndMonth: string = '';
  eventEndYear: string = '';

  selectedEventId: string = '';
  selectedSubEventId: string = '';
  actionSelected: string = '';

  constructor(
    public labelService: LabelService,
    private patientService: PatientService,
    public errorPanelService: ErrorPanelService
  ) {}

  ngOnInit(): void {
    if (this.significantEvents.length > 0) {
      this.significantEvents = this.sortEventsByDate([
        ...this.significantEvents,
      ]);
    }
  }

  private parseDate(dateStr: string): moment.Moment {
    return dateStr.includes('/')
      ? moment(
          dateStr,
          dateStr.split('/').length === 3 ? 'DD/MM/YYYY' : 'MM/YYYY'
        )
      : moment(dateStr);
  }

  private sortEventsByDate(events: EventLifeLine[]): EventLifeLine[] {
    // Sort sub-events first
    events.forEach((event) => {
      if (event.sub_events) {
        event.sub_events = event.sub_events.sort(
          (a, b) =>
            this.parseDate(a.start_date).valueOf() -
            this.parseDate(b.start_date).valueOf()
        );
      }
    });

    // Then sort main events
    return events.sort(
      (a, b) =>
        this.parseDate(a.start_date).valueOf() -
        this.parseDate(b.start_date).valueOf()
    );
  }

  toggleEventExpansion(eventId: string): void {
    if (this.expandedEvents[eventId]) {
      this.expandedEvents[eventId] = !this.expandedEvents[eventId];
    } else {
      this.expandedEvents[eventId] = true;
    }
  }

  editOrDeleteEvent(event: EventLifeLine) {
    switch (!this.hasModifyButtonBeenClicked) {
      case true:
        this.prepareEditEvent(event);
        break;
      case false:
        this.deleteEvent(event);
        break;
    }
  }

  editOrDeleteSubEvent(subEvent: SubEventLifeLine) {
    switch (!this.hasModifyButtonBeenClicked) {
      case true:
        this.prepareEditSubEvent(subEvent);
        break;
      case false:
        this.deleteSubEvent(subEvent);
        break;
    }
  }

  addSubEvent(event: EventLifeLine) {
    this.selectedEventId = event.id;
    this.actionSelected = 'add_sub_event';
    this.showEditPanel = true;
    this.panelTitle = 'Aggiungi Sotto Evento';
    this.eventStartDay = '';
    this.eventStartMonth = '';
    this.eventStartYear = '';
    this.eventEndDay = '';
    this.eventEndMonth = '';
    this.eventEndYear = '';
  }

  toggleEditEventPanel() {
    this.showEditPanel = !this.showEditPanel;
    this.actionSelected = 'edit_event';
    this.eventTitle = '';
    this.eventStartDay = '';
    this.eventStartMonth = '';
    this.eventStartYear = '';
    this.eventEndDay = '';
    this.eventEndMonth = '';
    this.eventEndYear = '';
  }

  prepareEditEvent(event: EventLifeLine) {
    this.selectedEventId = event.id;
    this.showEditPanel = true;
    this.panelTitle = 'Modifica Evento';
    this.actionSelected = 'edit_event';
    this.eventTitle = event.title;
    const startDate = event.start_date.split('/');
    if (startDate.length === 3) {
      this.eventStartDay = startDate[0];
      this.eventStartMonth = startDate[1];
      this.eventStartYear = startDate[2];
    }
    if (startDate.length === 2) {
      this.eventStartMonth = startDate[0];
      this.eventStartYear = startDate[1];
    }
    const endDate = event.end_date?.split('/');
    if (endDate && endDate.length === 3) {
      this.eventEndDay = endDate[0];
      this.eventEndMonth = endDate[1];
      this.eventEndYear = endDate[2];
    }
    if (endDate && endDate.length === 2) {
      this.eventEndMonth = endDate[0];
      this.eventEndYear = endDate[1];
    }
  }

  prepareEditSubEvent(subEvent: SubEventLifeLine) {
    this.selectedSubEventId = subEvent.id;
    this.showEditPanel = true;
    this.panelTitle = 'Modifica Sotto Evento';
    this.actionSelected = 'edit_sub_event';
    this.eventTitle = subEvent.title;
    const startDate = subEvent.start_date.split('/');
    if (startDate.length === 3) {
      this.eventStartDay = startDate[0];
      this.eventStartMonth = startDate[1];
      this.eventStartYear = startDate[2];
    }
    if (startDate.length === 2) {
      this.eventStartMonth = startDate[0];
      this.eventStartYear = startDate[1];
    }
  }

  editSignificantEvent(event: AddEventLifeLine) {
    switch (this.actionSelected) {
      case 'edit_event':
        const editEvent = {
          patient_id: this.patientId,
          ...event,
        };
        this.patientService
          .putLifeLineEvent(this.selectedEventId, editEvent)
          .subscribe({
            next: (response) => {
              this.toggleEditEventPanel();
              this.refreshEventList.emit();
            },
            error: (err) => {
              console.error(
                'Errore nel modificare l\evento significativo',
                err
              );
              this.toggleEditEventPanel();
              this.errorPanelService.showErrorSnackBar(
                "Errore nel modificare l'evento significativo"
              );
            },
          });
        break;

      case 'add_sub_event':
        const subEvent = [
          {
            event_id: this.selectedEventId,
            start_date: event.start_date,
            title: event.title,
          },
        ];
        this.patientService.createLifeLineSubEvent(subEvent).subscribe({
          next: (response) => {
            this.toggleEditEventPanel();
            this.refreshEventList.emit();
          },
          error: (err) => {
            console.error(
              'Errore nel creare il nuovo evento significativo',
              err
            );
            this.toggleEditEventPanel();
            this.errorPanelService.showErrorSnackBar(
              'Errore nel creare il nuovo evento significativo'
            );
          },
        });
        break;

      case 'edit_sub_event':
        const editSubEvent = {
          start_date: event.start_date,
          title: event.title,
        };
        this.patientService
          .putLifeLineSubEvent(this.selectedSubEventId, editSubEvent)
          .subscribe({
            next: (response) => {
              this.toggleEditEventPanel();
              this.refreshEventList.emit();
            },
            error: (err) => {
              console.error('Errore nel modificare il sotto evento', err);
              this.toggleEditEventPanel();
              this.errorPanelService.showErrorSnackBar(
                'Errore nel modificare il sotto evento'
              );
            },
          });
    }
  }

  deleteEvent(event: EventLifeLine) {
    this.patientService.deleteLifeLineEvent(event.id).subscribe({
      next: (response) => {
        const index = this.significantEvents.findIndex(
          (eve) => eve.id === event.id
        );

        if (index !== -1) {
          this.significantEvents.splice(index, 1);
        }
      },
      error: (err) => {
        console.error("Errore nella eliminazione dell' evento", err);
        this.errorPanelService.showErrorSnackBar(
          "Errore nella eliminazione dell'evento"
        );
      },
    });
  }

  deleteSubEvent(event: SubEventLifeLine) {
    this.patientService.deleteLifeLineSubEvent(event.id).subscribe({
      next: (response) => {
        this.refreshEventList.emit();
      },
      error: (err) => {
        console.error('Errore nella eliminazione del sotto evento', err);
        this.errorPanelService.showErrorSnackBar(
          'Errore nella eliminazione del sotto evento'
        );
      },
    });
  }

  onCancel() {
    this.showEditPanel = false;
  }

  onConfirm() {
    this.showEditPanel = false;
  }
}
